/**
 * Map image export for the Investments Widget.
 *
 * Exports the map as a PNG that always frames the full given bounds
 * (Germany), regardless of how the user has panned/zoomed and regardless of
 * the container's aspect ratio: the live map is temporarily re-fitted to the
 * bounds behind a small veil, composited — the MapLibre GL basemap canvas
 * plus the canvas-rendered choropleth and bubble layers — onto an offscreen
 * canvas, cropped to the bounds' pixel extent, and the previous view is
 * restored.
 *
 * No screenshot library: elements are drawn at their on-screen rects
 * (getBoundingClientRect relative to the map container), which already
 * accounts for Leaflet's pane transforms and fractional zoom scaling.
 * Requires the GL basemap to run with preserveDrawingBuffer (its canvas reads
 * back blank otherwise) and CORS-approved tile/glyph sources, otherwise the
 * canvas is tainted and toBlob() throws.
 */

import { GridLayer, LatLngBounds, latLngBounds } from 'leaflet';
import type { LatLngBoundsExpression, LatLngExpression, Map as LeafletMap } from 'leaflet';
import type { Map as MaplibreMap } from 'maplibre-gl';
import { downloadCanvasPng, resolveExportTokens, roundedRectPath, sourceText } from './canvasExport';
import { t } from '../i18n';

export interface LegendEntry {
  label: string;
  color: string;
  /** Type filtered out on the live map — rendered semi-transparent, like on screen. */
  dimmed?: boolean;
  /** Rectangular choropleth swatch instead of the round type dot. */
  swatch?: boolean;
}

export interface MapExportLegend {
  title: string;
  entries: LegendEntry[];
}

const EXPORT_SCALE = 2;
/** Margin (CSS px) around the bounds, both in the temporary fit and the crop. */
const EXPORT_PAD = 16;
const ATTRIBUTION = '© OpenFreeMap © OpenMapTiles © OpenStreetMap';

function paneZIndex(el: Element): number {
  const pane = el.closest('.leaflet-pane');
  if (!pane) return 0;
  const z = parseInt(getComputedStyle(pane).zIndex, 10);
  return Number.isNaN(z) ? 0 : z;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function nextFrame(): Promise<void> {
  return new Promise(resolve => requestAnimationFrame(() => resolve()));
}

/** Resolves once every tile layer has finished loading the current view. */
function tilesLoaded(map: LeafletMap, timeoutMs = 5000): Promise<void> {
  return new Promise(resolve => {
    const loading: GridLayer[] = [];
    map.eachLayer(layer => {
      if (layer instanceof GridLayer && layer.isLoading()) loading.push(layer);
    });
    if (loading.length === 0) {
      resolve();
      return;
    }
    let remaining = loading.length;
    // Tiles can fail (offline, blocked CDN) without ever firing 'load'; the
    // timeout lets the export proceed with whatever has arrived.
    const timer = setTimeout(resolve, timeoutMs);
    for (const layer of loading) {
      layer.once('load', () => {
        remaining -= 1;
        if (remaining === 0) {
          clearTimeout(timer);
          resolve();
        }
      });
    }
  });
}

/** The MapLibre GL map embedded by the GL basemap layer, if any. */
function findGlMap(map: LeafletMap): MaplibreMap | null {
  let gl: MaplibreMap | null = null;
  map.eachLayer(layer => {
    const getGlMap = (layer as { getMaplibreMap?: () => MaplibreMap }).getMaplibreMap;
    if (typeof getGlMap === 'function') gl = getGlMap.call(layer);
  });
  return gl;
}

/**
 * Resolves once the GL basemap has finished rendering the current view.
 * Resolves immediately without a GL layer (WebGL-less fallback); the timeout
 * keeps the export from hanging when the style host is unreachable.
 */
function glIdle(map: LeafletMap, timeoutMs = 5000): Promise<void> {
  const gl = findGlMap(map);
  if (!gl) return Promise.resolve();
  if (gl.loaded() && gl.areTilesLoaded()) return Promise.resolve();
  return new Promise(resolve => {
    const finish = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      gl.off('idle', finish);
      resolve();
    }, timeoutMs);
    gl.once('idle', finish);
  });
}

/** Map-legend text styling derived from the shared export tokens. */
interface ExportStyle {
  fontStack: string;
  font: string;
  titleFont: string;
  textPrimary: string;
  textSecondary: string;
}

function resolveExportStyle(container: HTMLElement): ExportStyle {
  const tokens = resolveExportTokens(container);
  // The on-screen legends render in theme.fontFamily = var(--serif-stack).
  return {
    fontStack: tokens.serifStack,
    font: `11px ${tokens.serifStack}`,
    titleFont: `600 11px ${tokens.serifStack}`,
    textPrimary: tokens.textPrimary,
    textSecondary: tokens.textSecondary,
  };
}

const LEGEND_PAD_X = 12;
const LEGEND_PAD_Y = 8;
const LEGEND_ROW_H = 17;
const LEGEND_TITLE_H = 18;

function legendSize(ctx: CanvasRenderingContext2D, legend: MapExportLegend, style: ExportStyle): { w: number; h: number } {
  ctx.font = style.titleFont;
  let textW = ctx.measureText(legend.title).width;
  ctx.font = style.font;
  for (const e of legend.entries) {
    // 16px marker column (dot/swatch + gap) before the label
    textW = Math.max(textW, 22 + ctx.measureText(e.label).width);
  }
  return {
    w: Math.ceil(textW) + LEGEND_PAD_X * 2,
    h: LEGEND_PAD_Y * 2 + LEGEND_TITLE_H + legend.entries.length * LEGEND_ROW_H,
  };
}

function drawLegend(ctx: CanvasRenderingContext2D, legend: MapExportLegend, style: ExportStyle, x: number, y: number, w: number, h: number): void {
  roundedRectPath(ctx, x, y, w, h, 6);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = 'rgba(24, 28, 68, 0.15)';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';

  ctx.font = style.titleFont;
  ctx.fillStyle = style.textPrimary;
  ctx.fillText(legend.title, x + LEGEND_PAD_X, y + LEGEND_PAD_Y + LEGEND_TITLE_H / 2);

  ctx.font = style.font;
  legend.entries.forEach((entry, i) => {
    const rowMidY = y + LEGEND_PAD_Y + LEGEND_TITLE_H + i * LEGEND_ROW_H + LEGEND_ROW_H / 2;
    ctx.globalAlpha = entry.dimmed ? 0.35 : 1;
    if (entry.swatch) {
      ctx.fillStyle = entry.color;
      ctx.fillRect(x + LEGEND_PAD_X, rowMidY - 6, 16, 12);
      ctx.strokeStyle = 'rgba(24, 28, 68, 0.2)';
      ctx.strokeRect(x + LEGEND_PAD_X, rowMidY - 6, 16, 12);
    } else {
      ctx.beginPath();
      ctx.arc(x + LEGEND_PAD_X + 5, rowMidY, 5, 0, Math.PI * 2);
      ctx.fillStyle = entry.color;
      ctx.fill();
    }
    ctx.fillStyle = style.textSecondary;
    ctx.fillText(entry.label, x + LEGEND_PAD_X + 22, rowMidY);
    ctx.globalAlpha = 1;
  });
}

function drawAttribution(ctx: CanvasRenderingContext2D, style: ExportStyle, width: number, height: number): void {
  ctx.font = `10px ${style.fontStack}`;
  const textW = ctx.measureText(ATTRIBUTION).width;
  const boxW = textW + 10;
  const boxH = 16;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.fillRect(width - boxW, height - boxH, boxW, boxH);
  ctx.fillStyle = 'rgba(24, 28, 68, 0.75)';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(ATTRIBUTION, width - boxW + 5, height - boxH / 2);
}

/** Source credit strip in the bottom-left corner, mirroring the attribution. */
function drawSourceLine(ctx: CanvasRenderingContext2D, style: ExportStyle, height: number): void {
  ctx.font = `10px ${style.fontStack}`;
  const source = sourceText();
  const textW = ctx.measureText(source).width;
  const boxH = 16;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.fillRect(0, height - boxH, textW + 10, boxH);
  ctx.fillStyle = 'rgba(24, 28, 68, 0.75)';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(source, 5, height - boxH / 2);
}

/**
 * Renders the map to a PNG framing `bounds` exactly and triggers a download.
 * Legends are passed in (right = investment types, left = choropleth volume
 * bins) because their data lives in component state, not on the Leaflet map.
 */
export async function exportMapImage(
  map: LeafletMap,
  bounds: LatLngBoundsExpression,
  legendRight: MapExportLegend | null,
  legendLeft: MapExportLegend | null,
): Promise<void> {
  const container = map.getContainer();

  // The snapshot needs the live map fitted to the export bounds; remember the
  // user's view to restore it afterwards, and cover the jump with a veil.
  const prevCenter = map.getCenter();
  const prevZoom = map.getZoom();

  const veil = document.createElement('div');
  veil.textContent = t().map.exporting;
  veil.style.cssText = `
    position: absolute; inset: 0; z-index: 1300;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255, 255, 255, 0.85);
    font: 600 14px var(--sans-stack, sans-serif); color: #181C44;
  `;
  // MapWrapper (position: relative) — same offset parent as the legends.
  (container.parentElement ?? container).appendChild(veil);

  // The GL basemap renders at the device pixel ratio; on 1x displays that
  // would export soft against EXPORT_SCALE, so raise it for the snapshot.
  const gl = findGlMap(map);
  const prevGlRatio = gl?.getPixelRatio();
  const bumpGlRatio = gl !== null && prevGlRatio !== undefined && prevGlRatio < EXPORT_SCALE;

  try {
    if (bumpGlRatio) gl.setPixelRatio(EXPORT_SCALE);
    map.fitBounds(bounds, { animate: false, padding: [EXPORT_PAD, EXPORT_PAD] });
    await tilesLoaded(map);
    await glIdle(map);
    // Leaflet fades new tiles in over 200ms and only then prunes the old-zoom
    // tiles; wait that out so the composite doesn't catch half-faded tiles.
    await delay(260);
    // The bubble/choropleth canvas renderers redraw on requestAnimationFrame.
    await nextFrame();
    await nextFrame();

    const size = map.getSize();
    const containerRect = container.getBoundingClientRect();

    // Crop vertically only: trim top/bottom so the bounds exactly span the
    // output height, but keep the container's full width — the horizontal
    // surroundings stay in frame, which also gives the corner legends room
    // over the neighboring countries instead of covering Germany.
    const b = bounds instanceof LatLngBounds ? bounds : latLngBounds(bounds as LatLngExpression[]);
    const nw = map.latLngToContainerPoint(b.getNorthWest());
    const se = map.latLngToContainerPoint(b.getSouthEast());
    const cropX = 0;
    const cropY = Math.max(0, Math.floor(Math.min(nw.y, se.y) - EXPORT_PAD));
    const cropW = size.x;
    const cropH = Math.min(size.y, Math.ceil(Math.max(nw.y, se.y) + EXPORT_PAD)) - cropY;

    const canvas = document.createElement('canvas');
    canvas.width = cropW * EXPORT_SCALE;
    canvas.height = cropH * EXPORT_SCALE;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.scale(EXPORT_SCALE, EXPORT_SCALE);

    // Basemap ground color so unloaded tile gaps don't export as transparency.
    ctx.fillStyle = '#d4dadc';
    ctx.fillRect(0, 0, cropW, cropH);
    ctx.translate(-cropX, -cropY);

    // The GL basemap canvas (nested inside the tile pane — the direct-child
    // selector below misses it), any raster tiles (img), and the vector
    // renderers (canvas — one per pane: states/bubbles in the overlay pane,
    // choropleth in its own "wahlkreise" pane). Document order follows pane
    // *creation* order, not stacking, so sort by the panes' z-index
    // (tiles 200 < wahlkreise 350 < overlay 400) to match the screen.
    const drawables = [
      ...container.querySelectorAll<HTMLCanvasElement>('.leaflet-tile-pane .maplibregl-canvas'),
      ...container.querySelectorAll<HTMLImageElement>('.leaflet-tile-pane img'),
      ...container.querySelectorAll<HTMLCanvasElement>('.leaflet-pane > canvas'),
    ]
      .filter(el => !(el instanceof HTMLImageElement) || (el.complete && el.naturalWidth > 0))
      .map((el, order) => ({ el, order, z: paneZIndex(el) }))
      .sort((a, b2) => a.z - b2.z || a.order - b2.order);

    for (const { el } of drawables) {
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      // drawImage ignores CSS opacity — honor it, so tiles still fading in
      // don't suddenly export fully opaque.
      const opacity = parseFloat(getComputedStyle(el).opacity);
      if (opacity === 0) continue;
      ctx.globalAlpha = Number.isNaN(opacity) ? 1 : opacity;
      ctx.drawImage(el, r.left - containerRect.left, r.top - containerRect.top, r.width, r.height);
    }
    ctx.globalAlpha = 1;

    // Overlays are drawn in crop-local coordinates.
    ctx.setTransform(EXPORT_SCALE, 0, 0, EXPORT_SCALE, 0, 0);
    const style = resolveExportStyle(container);
    // Legends mirror their on-screen corners (bottom 24px, inset 12px).
    if (legendRight && legendRight.entries.length > 0) {
      const { w, h } = legendSize(ctx, legendRight, style);
      drawLegend(ctx, legendRight, style, cropW - 12 - w, cropH - 24 - h, w, h);
    }
    if (legendLeft && legendLeft.entries.length > 0) {
      const { w, h } = legendSize(ctx, legendLeft, style);
      drawLegend(ctx, legendLeft, style, 12, cropH - 24 - h, w, h);
    }

    drawAttribution(ctx, style, cropW, cropH);
    drawSourceLine(ctx, style, cropH);

    await downloadCanvasPng(canvas, t().exports.mapFileName);
  } finally {
    if (bumpGlRatio) gl.setPixelRatio(prevGlRatio);
    map.setView(prevCenter, prevZoom, { animate: false });
    veil.remove();
  }
}
